import React, { useState } from "react";
import { Star } from "lucide-react";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { useProfileHub } from "../ProfileHubContext";
import { updateMyCommunityProfile } from "../../../api/communityService";

const CONDITION_LABEL = {
  games_played: "parties jouées",
  total_games: "parties jouées",
  correct_answers: "bonnes réponses",
  category_correct_answers: "bonnes réponses",
  wins: "victoires",
  first_places: "premières places",
  podiums: "podiums",
  total_score: "points",
  category_score: "points",
  category_games_played: "parties dans la catégorie",
  flawless_games: "parties sans faute",
};

function BadgeIcon({ badge }) {
  if (typeof badge.icon === "string" && badge.icon.startsWith("http")) {
    return <img src={badge.icon} alt="" className="h-8 w-8 object-contain" />;
  }
  return <span className="text-xl">{badge.icon || "*"}</span>;
}

function UnlockedBadgeTile({ badge, isFeatured, onFeature }) {
  const ownThreshold = badge.condition_value ?? 0;
  const hasNextProgress = badge.current_count != null && ownThreshold > 0;
  const label = CONDITION_LABEL[badge.condition_type] || "progression";
  const current = Math.min(badge.current_count ?? 0, ownThreshold);
  const threshold = ownThreshold;
  const pct = threshold > 0 ? Math.min(100, Math.round((current / threshold) * 100)) : 0;

  return (
    <article
      className={`flex items-start gap-3 rounded-2xl border px-4 py-3 transition ${
        isFeatured
          ? "border-amber-300 bg-amber-50 ring-2 ring-amber-200"
          : badge.color || "border-slate-100 bg-slate-50"
      }`}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white">
        <BadgeIcon badge={badge} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">{badge.name}</div>
        {badge.description && (
          <div className="mt-0.5 text-xs text-slate-400">{badge.description}</div>
        )}
        {hasNextProgress && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-[10px] text-slate-500">
              <span>
                {current.toLocaleString("fr-FR")} / {threshold.toLocaleString("fr-FR")} {label}
              </span>
              <span className="font-semibold">{pct}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/70">
              <div className="h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        title={isFeatured ? "Badge mis en avant" : "Mettre en avant ce badge"}
        onClick={() => onFeature(badge)}
        className={`ml-auto shrink-0 rounded-full p-1.5 transition ${
          isFeatured ? "text-amber-500" : "text-slate-300 hover:text-amber-400"
        }`}
      >
        <Star className="h-4 w-4" fill={isFeatured ? "currentColor" : "none"} />
      </button>
    </article>
  );
}

export default function ProfileBadgesSection() {
  const { displayBadges, profileIdentity, refreshProfile } = useProfileHub();
  const featuredId = profileIdentity.featured_badge?.id ?? null;
  const [saving, setSaving] = useState(false);

  const handleFeature = (badge) => {
    if (saving) return;
    const isCurrent = badge.dbId === featuredId;
    setSaving(true);
    updateMyCommunityProfile({
      featured_badge_id: isCurrent ? null : badge.dbId,
    })
      .then(() => refreshProfile())
      .catch(() => {})
      .finally(() => setSaving(false));
  };

  return (
    <div className="space-y-6">
      <ProfileSectionCard
        title="Badges obtenus"
        subtitle="Clique sur l'étoile pour mettre un badge en avant sur ton profil."
      >
        {displayBadges.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-500">
            Aucun badge débloqué pour l'instant.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {displayBadges.map((badge) => (
              <UnlockedBadgeTile
                key={badge.id}
                badge={badge}
                isFeatured={badge.dbId === featuredId}
                onFeature={handleFeature}
              />
            ))}
          </div>
        )}
      </ProfileSectionCard>
    </div>
  );
}
